import React, { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type KeyboardTypeOptions,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';

function PasswordVisibilityIcon({
  color,
  visible,
}: {
  color: string;
  visible: boolean;
}) {
  return (
    <View style={styles.visibilityIconRoot} accessibilityElementsHidden>
      <View style={[styles.visibilityEye, { borderColor: color }]}>
        <View style={[styles.visibilityPupil, { backgroundColor: color }]} />
      </View>
      {visible ? (
        <View style={[styles.visibilitySlash, { backgroundColor: color }]} />
      ) : null}
    </View>
  );
}

type Props = {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  error?: string;
  secureTextEntry?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  editable?: boolean;
  autoCorrect?: boolean;
  textContentType?:
    | 'none'
    | 'username'
    | 'password'
    | 'emailAddress'
    | 'newPassword'
    | 'oneTimeCode';
};

export function AppInput({
  label,
  value,
  onChangeText,
  placeholder,
  error,
  secureTextEntry,
  keyboardType,
  autoCapitalize,
  editable = true,
  autoCorrect,
  textContentType,
}: Props) {
  const palette = useAppColors();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const showPasswordToggle = Boolean(secureTextEntry);

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <View style={styles.inputRow}>
        <TextInput
          accessibilityLabel={label}
          accessibilityHint={error ? error : undefined}
          accessibilityState={{ disabled: !editable }}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={palette.textMuted}
          secureTextEntry={showPasswordToggle ? !passwordVisible : secureTextEntry}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          textContentType={textContentType}
          editable={editable}
          style={[
            styles.input,
            showPasswordToggle ? styles.inputWithToggle : null,
            {
              borderColor: error ? palette.error : palette.border,
              backgroundColor: palette.surface,
              color: palette.textPrimary,
            },
            Platform.OS === 'web' ? ({ outlineWidth: 0 } as const) : null,
          ]}
        />
        {showPasswordToggle ? (
          <Pressable
            onPress={() => setPasswordVisible(current => !current)}
            accessibilityRole="button"
            accessibilityLabel={
              passwordVisible ? 'Hide password' : 'Show password'
            }
            hitSlop={8}
            style={styles.visibilityButton}
          >
            <PasswordVisibilityIcon
              color={palette.textMuted}
              visible={passwordVisible}
            />
          </Pressable>
        ) : null}
      </View>
      {error ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm, width: '100%', maxWidth: '100%', minWidth: 0 },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  inputRow: {
    position: 'relative',
  },
  input: {
    ...typography.input,
    minHeight: 50,
    minWidth: 0,
    maxWidth: '100%',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
  },
  inputWithToggle: {
    paddingRight: 48,
  },
  visibilityButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 44,
    paddingHorizontal: spacing.md,
  },
  visibilityIconRoot: {
    width: 22,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  visibilityEye: {
    width: 20,
    height: 12,
    borderRadius: 10,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  visibilityPupil: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  visibilitySlash: {
    position: 'absolute',
    width: 22,
    height: 1.5,
    borderRadius: 1,
    transform: [{ rotate: '-35deg' }],
  },
  error: { ...typography.caption },
});
